import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Selling plan selection changes
 */
class ProductPrice extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {MutationObserver | undefined} */
  #rechargePriceObserver;
  /** @type {string} Storage key prefix for purchase option preference */
  static #STORAGE_KEY_PREFIX = 'purchaseOption_';

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) {
      return;
    }
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // Listen for selling plan changes
    this.#setupSellingPlanListener();

    // Check if there's a selling plan in URL on page load and update price if needed
    this.#checkInitialSellingPlan();

    // Align one-time purchase price to match subscription structure
    this.#alignOneTimePurchasePrice();

    // Align subscription price structure
    this.#alignSubscriptionPrice();

    // Also try to set up price observer in case alignment already succeeded
    setTimeout(() => {
      const oneTimeInput = document.querySelector('input[name="purchaseOption"][value="onetime"]');
      if (oneTimeInput) {
        const label = oneTimeInput.closest('label');
        if (label && (label.querySelector('.rc-radio__price') || label.querySelector('.rc-radio_price'))) {
          // Already aligned, set up observer
          this.#setupRechargePriceObserver();
        }
      }
    }, 500);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // Clean up selling plan listener
    this.#abortController?.abort();

    // Clean up Recharge price observer
    this.#rechargePriceObserver?.disconnect();
  }

  /**
   * Finds the product form, searching both closest and document-wide.
   * @returns {HTMLFormElement|null}
   * @private
   */
  #findProductForm() {
    // First try closest (in case form is a parent)
    let productForm = this.closest('form[data-type="add-to-cart-form"]');

    // If not found, search document-wide for the form
    if (!productForm) {
      productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    }

    // Also try finding form by product ID if we have one
    if (!productForm && this.dataset.productId) {
      // Try to find form that contains an input with the product's variant
      const variantInputs = document.querySelectorAll('form input[name="id"]');
      for (const input of variantInputs) {
        const form = input.closest('form');
        if (form) {
          productForm = form;
          break;
        }
      }
    }

    return productForm;
  }

  /**
   * Sets up a listener for selling plan selection changes.
   * @private
   */
  #setupSellingPlanListener() {
    // Try to find form, with retry logic since it might not be loaded yet
    let productForm = this.#findProductForm();

    if (!productForm) {
      // Retry after a delay in case form hasn't loaded yet
      setTimeout(() => {
        productForm = this.#findProductForm();
        if (productForm) {
          this.#setupSellingPlanListener();
        }
      }, 500);
      return;
    }

    // Create abort controller for cleanup
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Function to set up listeners once inputs are found
    const setupListeners = () => {
      // Recharge widget uses purchaseOption radio buttons
      // Note: These inputs might be outside the form, so search document-wide
      let purchaseOptionInputs = productForm.querySelectorAll('input[name="purchaseOption"]');
      if (purchaseOptionInputs.length === 0) {
        // If not found in form, search the entire document (Recharge widget might render outside form)
        purchaseOptionInputs = document.querySelectorAll('input[name="purchaseOption"]');
      }

      if (purchaseOptionInputs.length > 0) {
      // Listen for Recharge widget purchase option changes
      purchaseOptionInputs.forEach((input) => {
        input.addEventListener('change', () => {

          // Get selling plan ID for storage
          const hiddenSellingPlan = productForm.querySelector('input[name="selling_plan"]');
          const urlParams = new URLSearchParams(window.location.search);
          const urlSellingPlan = urlParams.get('selling_plan');
          const sellingPlanId = hiddenSellingPlan?.value || urlSellingPlan || '';

          // Save to localStorage immediately
          this.#savePurchaseOptionToStorage(input.value, input.value === 'subscription' ? sellingPlanId : '');

          // Update URL immediately to reflect purchase option change
          const url = new URL(window.location.href);
          if (input.value === 'subscription') {
            // Subscription selected - preserve or get selling_plan from form
            if (sellingPlanId) {
              url.searchParams.set('selling_plan', sellingPlanId);
            }
          } else {
            // One-time purchase selected - remove selling_plan from URL
            url.searchParams.delete('selling_plan');
          }
          window.history.replaceState({}, '', url.toString());

          // Wait a bit for Recharge to update the hidden selling_plan input
            setTimeout(() => {
              this.#updatePriceFromSellingPlan();
              // Re-align prices when option changes
              if (input.value === 'onetime') {
                setTimeout(() => this.#alignOneTimePurchasePrice(), 150);
              } else if (input.value === 'subscription') {
                setTimeout(() => this.#alignSubscriptionPrice(), 150);
              }
              // Update saving badges when purchase option changes
              // Pass the purchase option value directly to avoid relying on DOM state that might not be updated yet
              setTimeout(() => {
                this.#updateSavingBadges(input.value).catch(() => {});
              }, 400);
            }, 100);
          }, { signal });
        });

      // Also watch the hidden selling_plan input for changes (Recharge updates it programmatically)
      const hiddenSellingPlan = productForm.querySelector('input[name="selling_plan"]');
      if (hiddenSellingPlan) {
        // Use MutationObserver to watch for value changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
              // Update localStorage if subscription is selected
              const productForm = this.#findProductForm();
              const subscriptionRadio = productForm?.querySelector('input[name="purchaseOption"][value="subscription"]');
              if (subscriptionRadio?.checked && hiddenSellingPlan.value) {
                this.#savePurchaseOptionToStorage('subscription', hiddenSellingPlan.value);
              }

              this.#updatePriceFromSellingPlan();
            }
          });
        });
        observer.observe(hiddenSellingPlan, {
          attributes: true,
          attributeFilter: ['value']
        });

        // Also listen for input events (some browsers fire this)
        hiddenSellingPlan.addEventListener('input', () => {
          // Update localStorage if subscription is selected
          const productForm = this.#findProductForm();
          const subscriptionRadio = productForm?.querySelector('input[name="purchaseOption"][value="subscription"]');
          if (subscriptionRadio?.checked && hiddenSellingPlan.value) {
            this.#savePurchaseOptionToStorage('subscription', hiddenSellingPlan.value);
          }

          this.#updatePriceFromSellingPlan();
        }, { signal });
      }
      return true; // Successfully set up listeners
    } else {
      // Fallback: Listen to standard selling plan inputs (non-Recharge)
      const sellingPlanInputs = productForm.querySelectorAll(
        'input[name="selling_plan"], input[type="radio"][name*="selling_plan"], select[name="selling_plan"], input[data-selling-plan], [data-selling-plan-id]'
      );

      if (sellingPlanInputs.length > 0) {
        sellingPlanInputs.forEach((input) => {
          input.addEventListener('change', this.#handleSellingPlanChange.bind(this), { signal });
        });
        return true; // Successfully set up listeners
      } else {
        return false; // No inputs found yet
      }
    }
    };

    // Try to set up listeners immediately
    if (setupListeners()) {
      return; // Successfully set up, we're done
    }

    // If no inputs found, wait for Recharge widget to load
    // Use MutationObserver to watch for when Recharge widget adds the inputs
    // Since inputs might be outside the form, observe the section or document body
    const section = this.closest('.shopify-section') || document.body;
    const observer = new MutationObserver((mutations) => {
      // Check if purchaseOption inputs have appeared (search document-wide)
      let purchaseOptionInputs = productForm.querySelectorAll('input[name="purchaseOption"]');
      if (purchaseOptionInputs.length === 0) {
        purchaseOptionInputs = document.querySelectorAll('input[name="purchaseOption"]');
      }
      if (purchaseOptionInputs.length > 0) {
        observer.disconnect();
        setupListeners();
        // Also trigger initial price update if subscription is selected
        setTimeout(() => this.#updatePriceFromSellingPlan(), 200);
        // Align price structures
        setTimeout(() => {
          this.#alignOneTimePurchasePrice();
          this.#alignSubscriptionPrice();
        }, 200);
      }
    });

    // Observe the section (or document body) for child additions, since inputs might be outside the form
    observer.observe(section, {
      childList: true,
      subtree: true
    });

    // Also retry with timeouts as fallback
    let retryCount = 0;
    const maxRetries = 10;
    const retryInterval = setInterval(() => {
      retryCount++;
      // Try to set up listeners (this will search document-wide for inputs)
      if (setupListeners()) {
        clearInterval(retryInterval);
        observer.disconnect();
        // Trigger initial price update
        setTimeout(() => this.#updatePriceFromSellingPlan(), 200);
        // Align price structures
        setTimeout(() => {
          this.#alignOneTimePurchasePrice();
          this.#alignSubscriptionPrice();
        }, 200);
      } else if (retryCount >= maxRetries) {
        clearInterval(retryInterval);
        observer.disconnect();
      }
    }, 500);
  }

  /**
   * Gets the current purchase option state (one-time vs subscription).
   * Centralized method to eliminate code duplication.
   * @param {string|null} [forcedPurchaseOption] - Optional: 'onetime' or 'subscription' to force a specific purchase option
   * @returns {{ isSubscriptionSelected: boolean, sellingPlanId: string }}
   * @private
   */
  #getPurchaseOptionState(forcedPurchaseOption = null) {
    const productForm = this.#findProductForm();
    let isSubscriptionSelected = false;
    let sellingPlanId = '';

    // If purchase option is forced (e.g., from change event), use it directly
    if (forcedPurchaseOption === 'onetime') {
      isSubscriptionSelected = false;
      sellingPlanId = '';
    } else if (forcedPurchaseOption === 'subscription') {
      isSubscriptionSelected = true;
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');
      const hiddenSellingPlan = productForm?.querySelector('input[name="selling_plan"]');
      sellingPlanId = hiddenSellingPlan?.value || urlSellingPlan || '';
    } else {
      // Detect from DOM state
      const subscriptionRadio = productForm?.querySelector('input[name="purchaseOption"][value="subscription"]');
      const oneTimeRadio = productForm?.querySelector('input[name="purchaseOption"][value="onetime"]');

      // Check radio button states - one-time takes precedence if explicitly selected
      if (oneTimeRadio?.checked === true) {
        // One-time is explicitly selected
        isSubscriptionSelected = false;
      } else if (subscriptionRadio?.checked === true) {
        // Subscription is explicitly selected
        isSubscriptionSelected = true;
      }

      // Also check URL parameter in case Recharge widget hasn't loaded yet
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');

      // Get the selling plan ID from hidden input or URL
      const hiddenSellingPlan = productForm?.querySelector('input[name="selling_plan"]');
      sellingPlanId = hiddenSellingPlan?.value || urlSellingPlan || '';

      // Only assume subscription if radio is checked OR if we have selling_plan in URL AND no explicit one-time selection
      if (!isSubscriptionSelected && urlSellingPlan && !oneTimeRadio?.checked) {
        // Radio state unclear, but URL has selling_plan and one-time is not explicitly selected - might be initial load, assume subscription
        isSubscriptionSelected = true;
      } else if (oneTimeRadio?.checked === true) {
        // One-time is explicitly selected, clear selling_plan
        sellingPlanId = '';
        isSubscriptionSelected = false;
      } else if (!isSubscriptionSelected && !urlSellingPlan && !oneTimeRadio?.checked && !subscriptionRadio?.checked) {
        // DOM state is unclear (neither radio checked, no URL param) - check localStorage as fallback
        const stored = this.#getPurchaseOptionFromStorage();
        if (stored) {
          isSubscriptionSelected = stored.purchaseOption === 'subscription';
          if (isSubscriptionSelected && stored.sellingPlanId) {
            sellingPlanId = stored.sellingPlanId;
          } else {
            sellingPlanId = '';
          }
        }
      }
    }

    return { isSubscriptionSelected, sellingPlanId };
  }

  /**
   * Gets the purchase option preference from localStorage.
   * @returns {{ purchaseOption: string, sellingPlanId: string } | null}
   * @private
   */
  #getPurchaseOptionFromStorage() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    try {
      const key = `${ProductPrice.#STORAGE_KEY_PREFIX}${productId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
    }
    return null;
  }

  /**
   * Saves the purchase option preference to localStorage.
   * @param {string} purchaseOption - 'onetime' or 'subscription'
   * @param {string} [sellingPlanId=''] - The selling plan ID if subscription
   * @private
   */
  #savePurchaseOptionToStorage(purchaseOption, sellingPlanId = '') {
    const productId = this.dataset.productId;
    if (!productId) return;

    try {
      const key = `${ProductPrice.#STORAGE_KEY_PREFIX}${productId}`;
      localStorage.setItem(key, JSON.stringify({ purchaseOption, sellingPlanId }));
    } catch (e) {
    }
  }

  /**
   * Updates price based on current selling plan selection.
   * Works with both Recharge widget and standard Shopify subscriptions.
   * @private
   */
  #updatePriceFromSellingPlan() {
    const productForm = this.#findProductForm();
    if (!productForm) {
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      return;
    }

    // Get purchase option state using centralized method
    const { isSubscriptionSelected, sellingPlanId } = this.#getPurchaseOptionState();


    // Get current variant ID
    const variantInput = productForm.querySelector('input[name="id"]');
    const variantId = variantInput?.value || '';

    // Try to get variant data from variant picker to calculate price client-side
    const variantPicker = document.querySelector('variant-picker');

    let variantData = null;
    if (variantPicker) {
      const variantScript = variantPicker.querySelector('script[type="application/json"]');
      if (variantScript) {
        try {
          variantData = JSON.parse(variantScript.textContent);
        } catch (e) {
          // Could not parse variant data
        }
      }
    }

    // If we have variant data, use it for price calculation
    if (variantData) {
      // Always get one-time purchase price (base variant price)
      const oneTimePrice = variantData.price;
      const oneTimeCompareAtPrice = variantData.compare_at_price;

      // Try to get subscription price from allocation if available
      // Always calculate subscription price for display in Recharge widget, even when one-time is selected
      let subscriptionPrice = null;
      let subscriptionCompareAtPrice = null;

      if (variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {

        let allocation = null;

        // If we have a specific sellingPlanId, try to find the matching allocation
        if (sellingPlanId) {
          allocation = variantData.selling_plan_allocations.find((a) => {
            const allocationId = a.selling_plan?.id;
            if (!allocationId) return false;
            return allocationId == sellingPlanId || allocationId == parseInt(sellingPlanId) || String(allocationId) === String(sellingPlanId);
          });
        }

        // Always fall back to first allocation if no match found (ensures subscription price is always available)
        if (!allocation) {
          allocation = variantData.selling_plan_allocations[0];
        }

        if (allocation) {
          subscriptionPrice = allocation.price;
          // For subscriptions, always use variant's compare_at_price for the crossed-out price
          subscriptionCompareAtPrice = variantData.compare_at_price;
        }
      }

      // Update the main price display based on selection
      if (!isSubscriptionSelected) {
        this.#updatePriceDisplay(oneTimePrice, oneTimeCompareAtPrice);
      } else if (subscriptionPrice !== null) {
        this.#updatePriceDisplay(subscriptionPrice, subscriptionCompareAtPrice);
      }

      // Always update both Recharge widget prices with their respective values
      this.#updateRechargeWidgetPrice(
        oneTimePrice,
        oneTimeCompareAtPrice,
        subscriptionPrice,
        subscriptionCompareAtPrice
      );

      // Don't update saving badges here - let the purchase option change handler be the primary source
      // This prevents race conditions when switching purchase options

      return; // IMPORTANT: Don't fall through to server fetch
    } else {
      // No variant data available - need to fetch from server
    }

    // Fallback: Fetch from server if client-side calculation didn't work

    const url = new URL(window.location.href);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    }
    if (isSubscriptionSelected && sellingPlanId) {
      url.searchParams.set('selling_plan', sellingPlanId);
    } else {
      url.searchParams.delete('selling_plan');
    }

    window.history.replaceState({}, '', url.toString());
    this.#fetchPriceUpdate(url.toString());
    // Note: We don't update saving badges here because this method is called when variants change,
    // and badges should only update when purchase option changes, not when variants change.
  }

  /**
   * Updates the price display directly with given price values.
   * @param {number} price - Price in cents
   * @param {number} compareAtPrice - Compare at price in cents (optional)
   * @private
   */
  #updatePriceDisplay(price, compareAtPrice = null) {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    // Format prices
    const formattedPrice = this.#formatPrice(price);
    const formattedCompareAtPrice = compareAtPrice && compareAtPrice > price ? this.#formatPrice(compareAtPrice) : null;

    // Always show sale price first (on left), then crossed-out price (on right)
    const showSalePriceFirst = true;

    // Build new HTML
    let newHtml = '';
    if (formattedCompareAtPrice) {
      // Sale price first
      newHtml += `<span role="group"><span class="visually-hidden">Sale price</span><span class="price">${formattedPrice}</span></span>`;
      // Regular price after sale price (crossed out on the right)
      newHtml += `<span role="group"><span class="visually-hidden">Regular price</span><span class="compare-at-price">${formattedCompareAtPrice}</span></span>`;
    } else {
      newHtml += `<span class="price">${formattedPrice}</span>`;
    }

    priceContainer.innerHTML = newHtml;
  }

  /**
   * Formats a price in cents to currency string.
   * Uses Shopify's money format if available, otherwise formats manually.
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   * @private
   */
  #formatPrice(priceInCents) {
    // Try to use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(priceInCents);
    }

    // Get currency from page or use default
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    // Convert cents to dollars
    const priceInDollars = priceInCents / 100;

    // Format as currency
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(priceInDollars);
    } catch (e) {
      // Fallback to simple format
      return `$${priceInDollars.toFixed(2)}`;
    }
  }

  /**
   * Handles selling plan selection changes (for non-Recharge implementations).
   * @param {Event} event - The change event.
   * @private
   */
  #handleSellingPlanChange = (event) => {
    this.#updatePriceFromSellingPlan();
  };

  /**
   * Checks for selling plan in URL on initial page load and updates price if needed.
   * @private
   */
  #checkInitialSellingPlan() {
    // Wait a bit for Recharge widget to initialize
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const sellingPlanId = urlParams.get('selling_plan');

      // Get the product form to check if selling plan is selected
      const productForm = this.#findProductForm();
      if (!productForm) {
        // Retry once more after additional delay
        setTimeout(() => {
          const retryForm = this.#findProductForm();
          if (retryForm) {
            this.#checkInitialSellingPlan();
          }
        }, 1000);
        return;
      }

      // Check if Recharge subscription is selected
      const subscriptionRadio = productForm.querySelector('input[name="purchaseOption"][value="subscription"]');
      const hiddenSellingPlan = productForm.querySelector('input[name="selling_plan"]');

      const isSubscriptionSelected = subscriptionRadio?.checked === true;
      const hasSellingPlan = hiddenSellingPlan?.value || sellingPlanId;


      if (isSubscriptionSelected && hasSellingPlan) {
        // Save to localStorage for future variant changes
        const finalSellingPlanId = hiddenSellingPlan?.value || sellingPlanId || '';
        this.#savePurchaseOptionToStorage('subscription', finalSellingPlanId);
        // Selling plan is selected, trigger price update
        this.#updatePriceFromSellingPlan();
      } else if (hasSellingPlan && !isSubscriptionSelected) {
        // URL has selling_plan but radio not checked yet - might be initial load, save to localStorage
        const finalSellingPlanId = hiddenSellingPlan?.value || sellingPlanId || '';
        this.#savePurchaseOptionToStorage('subscription', finalSellingPlanId);
      }

      // Always update saving badges on initial load (async, don't await to avoid blocking)
      this.#updateSavingBadges().catch(() => {});
    }, 500);
  }

  /**
   * Aligns the subscription price to match the expected structure.
   * Extracts the price from the text and creates a separate span with class rc-radio__price.
   * @private
   */
  #alignSubscriptionPrice() {
    const alignPrice = () => {
      // Find the subscription input first, then get its label
      const subscriptionInput = document.querySelector('input[name="purchaseOption"][value="subscription"]');
      if (!subscriptionInput) {
        return false; // Not found yet
      }

      // Find the label containing this input, then find the rc-radio__label within it
      const label = subscriptionInput.closest('label');
      if (!label) {
        return false;
      }

      // Try both class name formats (Recharge uses single underscore, our code uses double underscore)
      let subscriptionLabel = label.querySelector('.rc-radio__label') || label.querySelector('.rc-radio_label');

      // If rc-radio__label doesn't exist, the text might be directly in the label
      // In that case, we need to wrap it in a rc-radio__label span
      if (!subscriptionLabel) {
        // Check if it already has the price separated (try both formats)
        if (label.querySelector('.rc-radio__price') || label.querySelector('.rc-radio_price')) {
          return true; // Already done
        }

        // Get all text nodes and the input
        const text = label.textContent.trim();
        // Subscription text might contain "Subscribe & save" or similar
        const priceMatch = text.match(/\$[\d,]+\.?\d*/);
        if (priceMatch) {
          const price = priceMatch[0];
          const labelText = text.replace(price, '').trim();

          // Preserve the input and wrap the text in rc-radio__label
          const inputClone = subscriptionInput.cloneNode(true);
          label.innerHTML = '';
          label.appendChild(inputClone);

          const rcRadioLabel = document.createElement('span');
          rcRadioLabel.className = 'rc-radio__label';
          rcRadioLabel.innerHTML = `
            <span class="rc-radio__subscription">${labelText}</span>
            <span class="rc-radio__price"> ${price}</span>
          `;
          label.appendChild(rcRadioLabel);

          return true;
        }
        return false;
      }

      // Check if already restructured (has rc-radio__price span - try both formats)
      if (subscriptionLabel.querySelector('.rc-radio__price') || subscriptionLabel.querySelector('.rc-radio_price')) {
        return true; // Already done
      }

      // Get the current text content
      const text = subscriptionLabel.textContent.trim();

      // Extract price using regex (matches $ followed by numbers, commas, and decimal)
      const priceMatch = text.match(/\$[\d,]+\.?\d*/);
      if (!priceMatch) {
        return true; // Don't retry if no price found
      }

      const price = priceMatch[0];
      const labelText = text.replace(price, '').trim();

      // Clear and restructure to match expected format
      subscriptionLabel.innerHTML = `
        <span class="rc-radio__subscription">${labelText}</span>
        <span class="rc-radio__price"> ${price}</span>
      `;

      return true; // Success
    };

    // Try immediately
    if (alignPrice()) {
      return;
    }

    // If not found, set up observer and retry logic
    const observer = new MutationObserver(() => {
      if (alignPrice()) {
        observer.disconnect();
      }
    });

    // Observe the document body for Recharge widget additions
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also retry with timeouts as fallback
    let retryCount = 0;
    const maxRetries = 10;
    const retryInterval = setInterval(() => {
      retryCount++;
      if (alignPrice()) {
        clearInterval(retryInterval);
        observer.disconnect();
      } else if (retryCount >= maxRetries) {
        clearInterval(retryInterval);
        observer.disconnect();
      }
    }, 500);
  }

  /**
   * Aligns the one-time purchase price to match the subscription structure.
   * Extracts the price from the text and creates a separate span with class rc-radio__price.
   * @private
   */
  #alignOneTimePurchasePrice() {
    const alignPrice = () => {
      // Find the one-time purchase input first, then get its label
      const oneTimeInput = document.querySelector('input[name="purchaseOption"][value="onetime"]');
      if (!oneTimeInput) {
        return false; // Not found yet
      }

      // Find the label containing this input, then find the rc-radio__label within it
      const label = oneTimeInput.closest('label');
      if (!label) {
        return false;
      }

      // Try both class name formats (Recharge uses single underscore, our code uses double underscore)
      let oneTimeLabel = label.querySelector('.rc-radio__label') || label.querySelector('.rc-radio_label');

      // If rc-radio__label doesn't exist, the text might be directly in the label
      // In that case, we need to wrap it in a rc-radio__label span
      if (!oneTimeLabel) {
        // Check if it already has the price separated (try both formats)
        if (label.querySelector('.rc-radio__price') || label.querySelector('.rc-radio_price')) {
          return true; // Already done
        }

        // Get all text nodes and the input
        const text = label.textContent.trim();
        if (text.includes('One-time purchase') && text.match(/\$[\d,]+\.?\d*/)) {
          const priceMatch = text.match(/\$[\d,]+\.?\d*/);
          if (priceMatch) {
            const price = priceMatch[0];
            const labelText = text.replace(price, '').trim();

            // Preserve the input and wrap the text in rc-radio__label
            const inputClone = oneTimeInput.cloneNode(true);
            label.innerHTML = '';
            label.appendChild(inputClone);

            const rcRadioLabel = document.createElement('span');
            rcRadioLabel.className = 'rc-radio__label';
            rcRadioLabel.innerHTML = `
              <span class="rc-radio__onetime">${labelText}</span>
              <span class="rc-radio__price"> ${price}</span>
            `;
            label.appendChild(rcRadioLabel);

            return true;
          }
        }
        return false;
      }

      // Check if already restructured (has rc-radio__price span - try both formats)
      if (oneTimeLabel.querySelector('.rc-radio__price') || oneTimeLabel.querySelector('.rc-radio_price')) {
        return true; // Already done
      }

      // Get the current text content
      const text = oneTimeLabel.textContent.trim();

      // Extract price using regex (matches $ followed by numbers, commas, and decimal)
      const priceMatch = text.match(/\$[\d,]+\.?\d*/);
      if (!priceMatch) {
        return true; // Don't retry if no price found
      }

      const price = priceMatch[0];
      const labelText = text.replace(price, '').trim();

      // Clear and restructure to match subscription format
      oneTimeLabel.innerHTML = `
        <span class="rc-radio__onetime">${labelText}</span>
        <span class="rc-radio__price"> ${price}</span>
      `;

      return true; // Success
    };

    // Try immediately
    if (alignPrice()) {
      // Set up observer to watch for Recharge price updates after successful alignment
      setTimeout(() => this.#setupRechargePriceObserver(), 100);
      return;
    }

    // If not found, set up observer and retry logic
    const observer = new MutationObserver(() => {
      if (alignPrice()) {
        observer.disconnect();
        // Set up observer to watch for Recharge price updates after successful alignment
        setTimeout(() => this.#setupRechargePriceObserver(), 100);
      }
    });

    // Observe the document body for Recharge widget additions
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also retry with timeouts as fallback
    let retryCount = 0;
    const maxRetries = 10;
    const retryInterval = setInterval(() => {
      retryCount++;
      if (alignPrice()) {
        clearInterval(retryInterval);
        observer.disconnect();
        // Set up observer to watch for Recharge price updates after successful alignment
        setTimeout(() => this.#setupRechargePriceObserver(), 100);
      } else if (retryCount >= maxRetries) {
        clearInterval(retryInterval);
        observer.disconnect();
      }
    }, 500);
  }

  /**
   * Sets up a MutationObserver to watch for Recharge widget price updates
   * and sync them to our restructured .rc-radio__price span.
   * @private
   */
  #setupRechargePriceObserver() {
    const oneTimeInput = document.querySelector('input[name="purchaseOption"][value="onetime"]');
    if (!oneTimeInput) {
      return;
    }

    const label = oneTimeInput.closest('label');
    if (!label) {
      return;
    }

    // Disconnect existing observer if any
    this.#rechargePriceObserver?.disconnect();

    // Set up MutationObserver to watch for Recharge price updates
    this.#rechargePriceObserver = new MutationObserver((mutations) => {
      // Check if the label text changed (Recharge updated the price)
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Extract price from the label text and sync to our .rc-radio__price span
          this.#syncRechargePriceToRestructured();
          break;
        }
      }
    });

    // Observe the label and its children for changes
    this.#rechargePriceObserver.observe(label, {
      childList: true,
      subtree: true,
      characterData: true
    });

  }

  /**
   * Syncs the price from Recharge's label text to our restructured .rc-radio__price span.
   * This is called when Recharge updates the price in the original structure.
   * Preserves the compare-at price when updating.
   * @private
   */
  #syncRechargePriceToRestructured() {
    const oneTimeInput = document.querySelector('input[name="purchaseOption"][value="onetime"]');
    if (!oneTimeInput) return;

    const label = oneTimeInput.closest('label');
    if (!label) return;

    // Try both class name formats
    const oneTimePriceSpan = label.querySelector('.rc-radio__price') || label.querySelector('.rc-radio_price');
    if (!oneTimePriceSpan) {
      // Not restructured yet, try to align it
      this.#alignOneTimePurchasePrice();
      return;
    }

    // Get the full label text (including our restructured spans)
    const labelText = label.textContent.trim();

    // Extract price from the label text using regex
    const priceMatch = labelText.match(/\$[\d,]+\.?\d*/);
    if (priceMatch) {
      const extractedPrice = priceMatch[0];

      // Get current price from span (excluding compare-at price if present)
      const currentPriceText = oneTimePriceSpan.childNodes[0]?.textContent?.trim() || oneTimePriceSpan.textContent.trim();

      // Only update if the price actually changed
      if (currentPriceText !== extractedPrice) {
        // Get variant data to access compare_at_price (always use current variant data)
        let compareAtPrice = null;
        const variantPicker = document.querySelector('variant-picker');
        if (variantPicker) {
          const variantScript = variantPicker.querySelector('script[type="application/json"]');
          if (variantScript) {
            try {
              const variantData = JSON.parse(variantScript.textContent);
              // Use variant's compare_at_price if it's greater than the variant's price
              if (variantData?.compare_at_price && variantData.compare_at_price > variantData.price) {
                // Convert cents to formatted price
                compareAtPrice = this.#formatPrice(variantData.compare_at_price);
              }
            } catch (e) {
            }
          }
        }

        // Update the price span content
        oneTimePriceSpan.innerHTML = ` ${extractedPrice}`;

        // Add compare-at price if available (always use variant data if available, as it's current)
        if (compareAtPrice) {
          const compareAtSpan = document.createElement('span');
          compareAtSpan.className = 'compare-at-price';
          compareAtSpan.textContent = ` ${compareAtPrice}`;
          oneTimePriceSpan.appendChild(compareAtSpan);
        }

      }
    }
  }

  /**
   * Fetches the updated price section.
   * @param {string} requestUrl - The URL to fetch from.
   * @private
   */
  #fetchPriceUpdate(requestUrl) {
    // Abort any pending request
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    // Find the parent Shopify section (the one with shopify-section- prefix)
    // The price element is inside a product-price component which is inside a section
    let section = this.closest('.shopify-section');
    if (!section) {
      return;
    }

    // Extract section ID from the section's ID attribute
    let sectionId = section.id;
    if (sectionId && sectionId.startsWith('shopify-section-')) {
      sectionId = sectionId.replace('shopify-section-', '');
    } else {
      // Try to get from dataset or attribute
      sectionId = section.dataset?.sectionId || section.getAttribute('section-id');
    }

    if (!sectionId) {
      return;
    }

    const url = new URL(requestUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    const fetchUrl = url.toString();

    fetch(fetchUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newPrice = html.querySelector(`product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`);
        const currentPrice = this.querySelector('[ref="priceContainer"]');

        if (newPrice && currentPrice) {
          const newPriceText = newPrice.textContent?.trim();
          const currentPriceText = currentPrice.textContent?.trim();

          if (newPriceText !== currentPriceText) {
            currentPrice.replaceWith(newPrice.cloneNode(true));
          }
        }
      })
      .catch(() => {
        // Silently handle errors
      });
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Get variant data first (before any early returns) so we can always update Recharge widget
    let variantData = event.detail.resource;

    // Fallback: if event doesn't have variant data, try to get it from the new HTML's variant picker script
    if (!variantData?.price && event.detail.data.html) {
      const variantPickerScript = event.detail.data.html.querySelector('variant-picker script[type="application/json"]');
      if (variantPickerScript) {
        try {
          variantData = JSON.parse(variantPickerScript.textContent);
        } catch (e) {
          // Could not parse variant data
        }
      }
    }

    // Check if subscription is selected - if so, recalculate price client-side
    // First check localStorage for immediate detection (before DOM is ready)
    let isSubscriptionSelected = false;
    let sellingPlanId = '';

    // Always check localStorage early (even if variantData not available) for immediate price calculation
    const stored = this.#getPurchaseOptionFromStorage();
    if (stored) {
      isSubscriptionSelected = stored.purchaseOption === 'subscription';
      sellingPlanId = stored.sellingPlanId || '';
    }

    // If we have subscription from localStorage and variant data, update price immediately (before DOM check)
    // This prevents flash of one-time price
    let priceUpdatedFromStorage = false;
    if (isSubscriptionSelected && sellingPlanId && variantData?.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
      let allocation = variantData.selling_plan_allocations.find((a) => {
        const allocationId = a.selling_plan?.id;
        if (!allocationId) return false;
        return allocationId == sellingPlanId || allocationId == parseInt(sellingPlanId) || String(allocationId) === String(sellingPlanId);
      });

      if (!allocation && variantData.selling_plan_allocations.length === 1) {
        allocation = variantData.selling_plan_allocations[0];
      }

      if (allocation) {
        const subscriptionPrice = allocation.price;
        const subscriptionCompareAtPrice = variantData.compare_at_price;
        // Update price display immediately to prevent flash
        this.#updatePriceDisplay(subscriptionPrice, subscriptionCompareAtPrice);
        priceUpdatedFromStorage = true;
      }
    }

    // Then check DOM state (which may override localStorage)
    const productForm = this.#findProductForm();
    if (productForm) {
      const domState = this.#getPurchaseOptionState();
      // Only use DOM state if it's definitive (radio buttons checked or URL params present)
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');
      const subscriptionRadio = productForm.querySelector('input[name="purchaseOption"][value="subscription"]');
      const oneTimeRadio = productForm.querySelector('input[name="purchaseOption"][value="onetime"]');

      // URL params and explicit radio selection take precedence over localStorage
      if (urlSellingPlan || subscriptionRadio?.checked || oneTimeRadio?.checked) {
        isSubscriptionSelected = domState.isSubscriptionSelected;
        sellingPlanId = domState.sellingPlanId;
      }
      // Otherwise, use localStorage value we already have

      if (isSubscriptionSelected && sellingPlanId) {
        // Subscription is selected, calculate price immediately from variant data

        // Calculate subscription price immediately from variant data if available
        let subscriptionPrice = null;
        let subscriptionCompareAtPrice = null;
        let priceUpdatedImmediately = false;

        // Only calculate if we haven't already updated from localStorage
        if (!priceUpdatedFromStorage && variantData?.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
          let allocation = variantData.selling_plan_allocations.find((a) => {
            const allocationId = a.selling_plan?.id;
            if (!allocationId) return false;
            return allocationId == sellingPlanId || allocationId == parseInt(sellingPlanId) || String(allocationId) === String(sellingPlanId);
          });

          if (!allocation && variantData.selling_plan_allocations.length === 1) {
            allocation = variantData.selling_plan_allocations[0];
          }

          if (allocation) {
            subscriptionPrice = allocation.price;
            subscriptionCompareAtPrice = variantData.compare_at_price;

            // Update price display immediately to prevent flash
            this.#updatePriceDisplay(subscriptionPrice, subscriptionCompareAtPrice);
            priceUpdatedImmediately = true;
          }
        } else if (priceUpdatedFromStorage) {
          // Already updated from localStorage, just get the price values for Recharge widget
          if (variantData?.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
            let allocation = variantData.selling_plan_allocations.find((a) => {
              const allocationId = a.selling_plan?.id;
              if (!allocationId) return false;
              return allocationId == sellingPlanId || allocationId == parseInt(sellingPlanId) || String(allocationId) === String(sellingPlanId);
            });

            if (!allocation && variantData.selling_plan_allocations.length === 1) {
              allocation = variantData.selling_plan_allocations[0];
            }

            if (allocation) {
              subscriptionPrice = allocation.price;
              subscriptionCompareAtPrice = variantData.compare_at_price;
            }
          }
          priceUpdatedImmediately = true; // Mark as updated so we don't fall back to delayed update
        }

        // Fallback: use delayed update if allocation not found or variant data not available
        if (!priceUpdatedImmediately) {
          setTimeout(() => this.#updatePriceFromSellingPlan(), 100);
        }

        // Still update Recharge widget prices even when subscription is selected
        if (variantData?.price) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.#alignOneTimePurchasePrice();
              this.#alignSubscriptionPrice();
              this.#updateRechargeWidgetPrice(
                variantData.price,
                variantData.compare_at_price,
                subscriptionPrice,
                subscriptionCompareAtPrice
              );
            });
          });
        }
        return;
      }
    }

    // No subscription, use server response
    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) {
      // Still try to update Recharge widget even if price elements not found
      if (variantData?.price) {
        // Try to get subscription price from allocation if available
        let subscriptionPrice = null;
        let subscriptionCompareAtPrice = null;

        if (variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
          // Use first allocation if available (or find by selling plan ID if we have it)
          const allocation = variantData.selling_plan_allocations[0];
          if (allocation) {
            subscriptionPrice = allocation.price;
            subscriptionCompareAtPrice = variantData.compare_at_price;
          }
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#alignOneTimePurchasePrice();
            this.#alignSubscriptionPrice();
            this.#updateRechargeWidgetPrice(
              variantData.price,
              variantData.compare_at_price,
              subscriptionPrice,
              subscriptionCompareAtPrice
            );
          });
        });
      }
      return;
    }

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Always update the Recharge widget price when variant changes (even if price HTML didn't change)
    if (variantData?.price) {
      // Try to get subscription price from allocation if available
      let subscriptionPrice = null;
      let subscriptionCompareAtPrice = null;

      if (variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
        // Use first allocation if available (or find by selling plan ID if we have it)
        const allocation = variantData.selling_plan_allocations[0];
        if (allocation) {
          subscriptionPrice = allocation.price;
          subscriptionCompareAtPrice = variantData.compare_at_price;
        }
      }

      // Wait a bit for the DOM to settle and Recharge widget to finish any updates
      // Also re-align the price structure in case Recharge widget re-rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Re-align price structure in case Recharge widget re-rendered
          this.#alignOneTimePurchasePrice();
          this.#alignSubscriptionPrice();
          // Then update the price
          this.#updateRechargeWidgetPrice(
            variantData.price,
            variantData.compare_at_price,
            subscriptionPrice,
            subscriptionCompareAtPrice
          );
        });
      });
    }
  }

  /**
   * Updates the Recharge widget price display when variant changes.
   * Updates both one-time purchase and subscription prices with their respective values.
   * @param {number} oneTimePriceInCents - One-time purchase price in cents
   * @param {number|null} oneTimeCompareAtPriceInCents - One-time compare at price in cents (optional)
   * @param {number|null} subscriptionPriceInCents - Subscription price in cents (optional, will use one-time price if not provided)
   * @param {number|null} subscriptionCompareAtPriceInCents - Subscription compare at price in cents (optional)
   * @private
   */
  #updateRechargeWidgetPrice(oneTimePriceInCents, oneTimeCompareAtPriceInCents = null, subscriptionPriceInCents = null, subscriptionCompareAtPriceInCents = null) {

    // Use one-time price for subscription if not provided (fallback)
    const subPrice = subscriptionPriceInCents !== null ? subscriptionPriceInCents : oneTimePriceInCents;
    const subCompareAtPrice = subscriptionCompareAtPriceInCents !== null ? subscriptionCompareAtPriceInCents : oneTimeCompareAtPriceInCents;

    // Format one-time prices
    const formattedOneTimePrice = this.#formatPrice(oneTimePriceInCents);
    const formattedOneTimeCompareAtPrice = oneTimeCompareAtPriceInCents && oneTimeCompareAtPriceInCents > oneTimePriceInCents
      ? this.#formatPrice(oneTimeCompareAtPriceInCents)
      : null;

    // Format subscription prices
    const formattedSubscriptionPrice = this.#formatPrice(subPrice);
    const formattedSubscriptionCompareAtPrice = subCompareAtPrice && subCompareAtPrice > subPrice
      ? this.#formatPrice(subCompareAtPrice)
      : null;

    // Update one-time purchase price
    const oneTimeInput = document.querySelector('input[name="purchaseOption"][value="onetime"]');
    if (oneTimeInput) {
      const oneTimeLabel = oneTimeInput.closest('label');
      if (oneTimeLabel) {
        // Try both class names (Recharge uses single underscore, our code uses double underscore)
        let oneTimePriceSpan = oneTimeLabel.querySelector('.rc-radio__price') || oneTimeLabel.querySelector('.rc-radio_price');
        if (!oneTimePriceSpan) {
          // Not restructured yet, try to align it first
          this.#alignOneTimePurchasePrice();
          // Try again after a short delay
          setTimeout(() => {
            const retrySpan = oneTimeLabel.querySelector('.rc-radio__price') || oneTimeLabel.querySelector('.rc-radio_price');
            if (retrySpan && retrySpan instanceof HTMLElement) {
              this.#updatePriceSpanWithCompareAt(retrySpan, formattedOneTimePrice, formattedOneTimeCompareAtPrice);
            }
          }, 200);
        } else if (oneTimePriceSpan instanceof HTMLElement) {
          this.#updatePriceSpanWithCompareAt(oneTimePriceSpan, formattedOneTimePrice, formattedOneTimeCompareAtPrice);
        }
      }
    }

    // Update subscription price
    const subscriptionInput = document.querySelector('input[name="purchaseOption"][value="subscription"]');
    if (subscriptionInput) {
      const subscriptionLabel = subscriptionInput.closest('label');
      if (subscriptionLabel) {
        // Try both class names (Recharge uses single underscore, our code uses double underscore)
        let subscriptionPriceSpan = subscriptionLabel.querySelector('.rc-radio__price') || subscriptionLabel.querySelector('.rc-radio_price');
        if (!subscriptionPriceSpan) {
          // Not restructured yet, try to align it first
          this.#alignSubscriptionPrice();
          // Try again after a short delay
          setTimeout(() => {
            const retrySpan = subscriptionLabel.querySelector('.rc-radio__price') || subscriptionLabel.querySelector('.rc-radio_price');
            if (retrySpan && retrySpan instanceof HTMLElement) {
              this.#updatePriceSpanWithCompareAt(retrySpan, formattedSubscriptionPrice, formattedSubscriptionCompareAtPrice);
            }
          }, 200);
        } else if (subscriptionPriceSpan instanceof HTMLElement) {
          this.#updatePriceSpanWithCompareAt(subscriptionPriceSpan, formattedSubscriptionPrice, formattedSubscriptionCompareAtPrice);
        }
      }
    }
  }

  /**
   * Updates a price span with compare-at price if available.
   * @param {HTMLElement} priceSpan - The price span element
   * @param {string} formattedPrice - Formatted price string
   * @param {string|null} formattedCompareAtPrice - Formatted compare-at price string (optional)
   * @private
   */
  #updatePriceSpanWithCompareAt(priceSpan, formattedPrice, formattedCompareAtPrice) {
    // Ensure priceSpan is an HTMLElement
    if (!(priceSpan instanceof HTMLElement)) {
      return;
    }

    // Remove existing compare-at price if any (look inside the price span)
    const existingCompareAt = priceSpan.querySelector('.compare-at-price');
    if (existingCompareAt) {
      existingCompareAt.remove();
    }

    // Update the price span content
    priceSpan.innerHTML = ` ${formattedPrice}`;

    // Add compare-at price if available (inside the price span)
    if (formattedCompareAtPrice) {
      const compareAtSpan = document.createElement('span');
      compareAtSpan.className = 'compare-at-price';
      compareAtSpan.textContent = ` ${formattedCompareAtPrice}`;
      // Append inside the price span
      priceSpan.appendChild(compareAtSpan);
    }
  }

  /**
   * Fetches variant data for a specific variant ID.
   * @param {string} variantId - The variant ID to fetch data for
   * @param {string} sectionId - The section ID for the request
   * @param {string} productUrl - The product URL
   * @returns {Promise<object|null>} The variant data or null if fetch fails
   * @private
   */
  async #fetchVariantData(variantId, sectionId, productUrl) {
    if (!variantId || !sectionId || !productUrl) {
      return null;
    }

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);
      // Don't include selling_plan parameter - we want all allocations for this variant

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const variantScript = doc.querySelector('variant-picker script[type="application/json"]');

      if (variantScript) {
        const variantData = JSON.parse(variantScript.textContent);
        return variantData;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Hides all saving badges immediately to prevent flash of incorrect prices.
   * @private
   */
  #hideSavingBadges() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    const savingBadges = variantPicker.querySelectorAll('.variant-option__saving-badge');
    savingBadges.forEach((badge) => {
      if (badge instanceof HTMLElement) {
        badge.style.display = 'none';
      }
    });
  }

  /**
   * Updates saving badges in the variant picker based on current purchase option and prices.
   * Shows/hides badges and updates their text based on compare_at_price vs price.
   * @param {string|null} [forcedPurchaseOption] - Optional: 'onetime' or 'subscription' to force a specific purchase option instead of detecting from DOM
   * @private
   */
  async #updateSavingBadges(forcedPurchaseOption = null) {

    // Find all saving badges in the variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      return;
    }

    const savingBadges = variantPicker.querySelectorAll('.variant-option__saving-badge');
    if (savingBadges.length === 0) {
      return;
    }


    // Get section ID and product URL for fetching variant data
    const section = this.closest('.shopify-section');
    let sectionId = section?.id;
    if (sectionId && sectionId.startsWith('shopify-section-')) {
      sectionId = sectionId.replace('shopify-section-', '');
    } else {
      sectionId = section?.dataset?.sectionId || variantPicker.dataset.sectionId || '';
    }

    const productUrl = variantPicker.dataset.productUrl || window.location.pathname;
    if (!sectionId || !productUrl) {
      return;
    }

    // Get purchase option state - check localStorage first for immediate detection
    let isSubscriptionSelected = false;
    let sellingPlanId = '';

    // If purchase option is forced, use it directly
    if (forcedPurchaseOption) {
      const state = this.#getPurchaseOptionState(forcedPurchaseOption);
      isSubscriptionSelected = state.isSubscriptionSelected;
      sellingPlanId = state.sellingPlanId;
    } else {
      // Check localStorage first for immediate detection (before DOM check)
      const stored = this.#getPurchaseOptionFromStorage();
      if (stored) {
        isSubscriptionSelected = stored.purchaseOption === 'subscription';
        sellingPlanId = stored.sellingPlanId || '';
      }

      // Then check DOM state (which may override localStorage)
      const domState = this.#getPurchaseOptionState();
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');
      const productForm = this.#findProductForm();
      const subscriptionRadio = productForm?.querySelector('input[name="purchaseOption"][value="subscription"]');
      const oneTimeRadio = productForm?.querySelector('input[name="purchaseOption"][value="onetime"]');

      // URL params and explicit radio selection take precedence over localStorage
      if (urlSellingPlan || subscriptionRadio?.checked || oneTimeRadio?.checked) {
        isSubscriptionSelected = domState.isSubscriptionSelected;
        sellingPlanId = domState.sellingPlanId;
      }
      // Otherwise, use localStorage value we already have
    }


    // Process each saving badge - fetch variant data for each variant ID
    const badgeUpdates = Array.from(savingBadges).map(async (badge) => {
      const variantId = badge.getAttribute('data-variant-id');
      if (!variantId) {
        if (badge instanceof HTMLElement) {
          badge.style.display = 'none';
        }
        return;
      }


      // Fetch variant data for this specific variant ID
      const variantData = await this.#fetchVariantData(variantId, sectionId, productUrl);

      if (!variantData) {
        if (badge instanceof HTMLElement) {
          badge.style.display = 'none';
        }
        return;
      }

      const compareAtPrice = variantData.compare_at_price || 0;
      const variantPrice = variantData.price || 0;

      let price = 0;
      let shouldShowBadge = false;
      let savings = 0;

      if (isSubscriptionSelected && variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
        // Subscription selected - use the first allocation for THIS variant
        const allocation = variantData.selling_plan_allocations[0];

        if (allocation) {
          price = allocation.price;
          // For subscriptions: first try compare_at_price (MSRP savings)
          // Fall back to variant_price if compare_at doesn't provide savings
          if (compareAtPrice > price) {
            shouldShowBadge = true;
            savings = compareAtPrice - price;
          } else if (variantPrice > price) {
            // Fallback: use variant price if compare_at doesn't provide savings
            // This handles cases where compare_at_price equals variant_price
            shouldShowBadge = true;
            savings = variantPrice - price;
          } else {
            shouldShowBadge = false;
          }
        } else {
          shouldShowBadge = false;
        }
      } else {
        // One-time purchase - use variant base price
        price = variantPrice;

        if (compareAtPrice > price) {
          shouldShowBadge = true;
          savings = compareAtPrice - price;
        }
      }

      // Update badge visibility and text
      if (shouldShowBadge && savings > 0) {
        const formattedSavings = this.#formatPrice(savings);
        badge.textContent = `Save ${formattedSavings}`;
        if (badge instanceof HTMLElement) {
          badge.style.display = '';
        }
      } else {
        if (badge instanceof HTMLElement) {
          badge.style.display = 'none';
        }
      }
    });

    // Wait for all badge updates to complete
    await Promise.all(badgeUpdates);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
